"use client";

import React, { useEffect, useState, useContext } from "react";
import ReactDOM from "react-dom";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import {
  fetchBookingsByDomeAndDate,
  approveBooking,
  createHostPanelBooking,
} from "../../api/booking.api";
import { fetchSessionsByDome, fetchSessionBookingStatus } from "../../api/session.api";
import Swal from "sweetalert2";

const DomeBookings = () => {
  const { domeid: domeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const domeData = location.state?.domeData || null;

  const { background } = useContext(ThemeContext);
  const isDark = background === "dark";

  // Determine if this is a private/corporate dome
  const isHostManagedDome =
    domeData?.domeType === "private" || domeData?.domeType === "corporate";

  // Helper to reliably compute the "YYYY-MM-DD" string for the dome's timezone
  const getDomeTodayDateString = () => {
    const domeTimezone = domeData?.timezone || "UTC";
    let nowInDome;
    try {
      nowInDome = new Date(new Date().toLocaleString("en-US", { timeZone: domeTimezone }));
    } catch (e) {
      nowInDome = new Date();
    }
    const year = nowInDome.getFullYear();
    const month = String(nowInDome.getMonth() + 1).padStart(2, "0");
    const day = String(nowInDome.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });

  // Create booking modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [createForm, setCreateForm] = useState({ date: "", sessionRef: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  /* ---------------------- FETCH BOOKINGS ---------------------- */
  const loadBookings = async (selectedDate = "", page = 1) => {
    try {
      setLoading(true);
      const data = await fetchBookingsByDomeAndDate(
        domeId,
        selectedDate || "",
        page
      );
      setBookings(data.bookings || []);
      if (data.pagination) {
        setPagination({
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          total: data.pagination.total,
        });
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError("Failed to load bookings. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- FETCH SESSIONS FOR DROPDOWN ---------------------- */
  const loadSessions = async (date) => {
    if (!domeId || !date) {
      setSessions([]);
      return;
    }
    setSessionsLoading(true);
    try {
      const data = await fetchSessionBookingStatus(domeId, date);
      // Filter out only available sessions
      const available = (data?.sessions || data || []).filter(s => s.status === "available");
      setSessions(available);
    } catch (err) {
      console.error("Error loading sessions:", err);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (showCreateModal && createForm.date) {
      loadSessions(createForm.date);
    } else {
      setSessions([]);
    }
  }, [createForm.date, showCreateModal]);

  /* ---------------------- INITIAL + FILTER ---------------------- */
  useEffect(() => {
    loadBookings(dateFilter, pagination.currentPage);
  }, [domeId, dateFilter, pagination.currentPage]);

  /* ---------------------- PAGE HANDLERS ---------------------- */
  const handlePrevPage = () => {
    if (pagination.currentPage > 1) {
      setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }));
    }
  };

  const handleNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }));
    }
  };

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Approve Booking?",
      text: "This will allow the user to check-in on the tablet.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, approve",
    });

    if (result.isConfirmed) {
      try {
        await approveBooking(id);
        Swal.fire("Approved!", "Booking has been approved for entry.", "success");
        loadBookings(dateFilter, pagination.currentPage);
      } catch (error) {
        Swal.fire("Error", "Failed to approve booking.", "error");
      }
    }
  };

  /* ---------------------- OPEN CREATE MODAL ---------------------- */
  const handleOpenCreateModal = async () => {
    setCreateForm({ date: "", sessionRef: "" });
    setCreateError(null);
    setShowCreateModal(true);
  };

  /* ---------------------- SUBMIT CREATE BOOKING ---------------------- */
  const handleCreateBooking = async () => {
    if (!createForm.date || !createForm.sessionRef) {
      setCreateError("Please select both a date and a session slot.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await createHostPanelBooking(domeId, createForm.sessionRef, createForm.date);
      setShowCreateModal(false);
      Swal.fire("Booking Created!", "The session has been booked and is auto-approved.", "success");
      loadBookings(dateFilter, pagination.currentPage);
    } catch (err) {
      setCreateError(
        err?.message || "Failed to create booking. Please try again."
      );
    } finally {
      setCreating(false);
    }
  };

  // Returns true if the session's UTC end time has already passed
  // booking.date = UTC start time; booking.duration = seconds (e.g. 1200 = 20 min)
  const isSessionExpired = (booking) => {
    try {
      if (!booking.date) return false;
      const startMs = new Date(booking.date).getTime();
      // duration is stored in seconds (1200 = 20 min); fall back to 20 min if missing
      const durationMs = ((booking.duration || 1200) > 100
        ? (booking.duration || 1200)   // already in seconds
        : (booking.duration || 20) * 60 // in minutes, convert to seconds
      ) * 1000;
      const endTime = new Date(startMs + durationMs);
      return endTime < new Date();
    } catch (e) {
      return false;
    }
  };

  const getBookingStatus = (booking) => {
    if (booking.status === "expired") return <span className="badge badge-danger">Unfinished</span>;
    if (booking.status === "completed") return <span className="badge badge-success">Completed</span>;
    if (booking.userChecked) return <span className="badge badge-success">Started</span>;
    if (booking.tabletChecked) return <span className="badge badge-info text-white">In Dome</span>;
    // Session time has passed and user never showed up (approved or not)
    if (isSessionExpired(booking)) return <span className="badge badge-danger">Unfinished</span>;
    if (booking.hostApproved) return <span className="badge badge-primary">Approved</span>;
    return <span className="badge badge-warning text-dark">Waiting</span>;
  };

  const filteredSessions = sessions.filter((s) => {
    if (!createForm.date) return true;

    const selectedDateStr = createForm.date;
    const domeTimezone = domeData?.timezone || "UTC";

    // Get current time in dome's timezone
    let nowInDome;
    try {
      nowInDome = new Date(new Date().toLocaleString("en-US", { timeZone: domeTimezone }));
    } catch (e) {
      nowInDome = new Date(); // Fallback to local time if timezone is invalid
    }

    const domeTodayStr = getDomeTodayDateString();

    // Only filter if the user selected the dome's "today"
    if (selectedDateStr === domeTodayStr) {
      if (!s.startTime) return true;
      try {
        const timeStr = s.startTime.toUpperCase().trim();
        let sessionHours = 0;
        let sessionMinutes = 0;

        if (timeStr.includes("AM") || timeStr.includes("PM")) {
          const [time, meridian] = timeStr.split(/\s+/);
          const parts = time.split(":");
          sessionHours = parseInt(parts[0], 10);
          sessionMinutes = parseInt(parts[1], 10) || 0;
          if (meridian === "PM" && sessionHours < 12) sessionHours += 12;
          if (meridian === "AM" && sessionHours === 12) sessionHours = 0;
        } else {
          const parts = timeStr.split(":");
          sessionHours = parseInt(parts[0], 10);
          sessionMinutes = parseInt(parts[1], 10) || 0;
        }

        const currentHours = nowInDome.getHours();
        const currentMinutes = nowInDome.getMinutes();

        if (sessionHours < currentHours) return false;
        if (sessionHours === currentHours && sessionMinutes <= currentMinutes) return false;
      } catch (e) {
        return true;
      }
    }
    return true;
  });

  return (
    <div className="container-fluid py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <div className="d-flex align-items-center mb-3">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline-secondary me-3"
          >
            <i className="fas fa-arrow-left me-2"></i>Back
          </button>
          <h4 className="fs-20 mb-0">
            Bookings for{" "}
            <span className="text-primary">{domeData?.domeName || "N/A"}</span>
            {isHostManagedDome && (
              <span
                className="badge ms-2"
                style={{
                  fontSize: "11px",
                  background: domeData?.domeType === "corporate" ? "#6f42c1" : "#0dcaf0",
                  color: "#fff",
                  textTransform: "capitalize",
                }}
              >
                {domeData.domeType}
              </span>
            )}
          </h4>
        </div>

        <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
          {/* Create Booking — only for private/corporate domes */}
          {isHostManagedDome && (
            <button
              className="btn btn-success"
              onClick={handleOpenCreateModal}
              style={{ fontSize: "14px" }}
            >
              <i className="fas fa-plus me-1"></i> Create Booking
            </button>
          )}

          {/* Date Filter */}
          <input
            type="date"
            className="form-control"
            style={{ width: "200px", fontSize: "14px", borderRadius: "5px" }}
            value={dateFilter}
            onChange={(e) => {
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
              setDateFilter(e.target.value);
            }}
          />
          {dateFilter && (
            <button
              className="btn btn-outline-secondary"
              style={{ fontSize: "13px" }}
              onClick={() => {
                setDateFilter("");
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && <div className="alert alert-danger text-center">{error}</div>}

      {/* Table */}
      {!loading && !error && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body p-0" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)" }}>
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                  <table
                    className={`table align-middle mb-0 table-responsive-mobile ${isDark ? "table-dark table-striped" : "table-striped"
                      }`}
                    style={{ whiteSpace: "nowrap", fontSize: "13px" }}
                  >
                    <thead className={`${isDark ? "bg-secondary" : "bg-light"}`}>
                      <tr>
                        <th style={{ padding: "8px 10px" }}>#</th>
                        <th style={{ padding: "8px 10px" }}>User</th>
                        <th style={{ padding: "8px 10px" }}>Email</th>
                        {!isHostManagedDome && <th style={{ padding: "8px 10px" }}>Phone</th>}
                        <th style={{ padding: "8px 10px" }}>Session</th>
                        <th style={{ padding: "8px 10px" }}>Start Time</th>
                        <th style={{ padding: "8px 10px" }}>Duration</th>
                        <th style={{ padding: "8px 10px" }}>Date</th>
                        {/* <th style={{ padding: "8px 10px" }}>Source</th> */}
                        <th style={{ padding: "8px 10px" }}>Status</th>
                        <th style={{ padding: "8px 10px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings?.length === 0 ? (
                        <tr>
                          <td colSpan={isHostManagedDome ? "9" : "10"} className="text-center py-4 text-muted">
                            <i className="fas fa-calendar-times me-2"></i>No bookings found.
                          </td>
                        </tr>
                      ) : (
                        bookings?.map((booking, index) => (
                          <tr key={booking._id}>
                            <td style={{ padding: "8px 10px" }} data-label="#">{(pagination.currentPage - 1) * 10 + index + 1}</td>
                            <td style={{ padding: "8px 10px" }} data-label="User">{booking?.name || "Guest"}</td>
                            <td style={{ padding: "8px 10px" }} data-label="Email">{booking?.email || "N/A"}</td>
                            {!isHostManagedDome && <td style={{ padding: "8px 10px" }} data-label="Phone">{booking?.phone || "N/A"}</td>}
                            <td style={{ padding: "8px 10px" }} data-label="Session">{booking.sessionRef?.name || "N/A"}</td>
                            <td style={{ padding: "8px 10px" }} data-label="Start Time">{booking.sessionRef?.startTime || "N/A"}</td>
                            <td style={{ padding: "8px 10px" }} data-label="Duration">{booking.sessionRef?.duration || "N/A"}</td>
                            <td style={{ padding: "8px 10px" }} data-label="Date">
                              {booking.displayDate ||
                                (booking.date
                                  ? (function () {
                                    try {
                                      return new Intl.DateTimeFormat("en-GB", {
                                        timeZone: domeData?.timezone || "UTC",
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric"
                                      }).format(new Date(booking.date));
                                    } catch (e) {
                                      // Fallback to local browser timezone if domeData.timezone is invalid
                                      return new Date(booking.date).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric"
                                      });
                                    }
                                  })()
                                  : "N/A")}
                            </td>
                            {/* <td data-label="Source">
                        {booking.bookingSource === "host_panel" ? (
                          <span className="badge badge-info text-dark" style={{ fontSize: "11px" }}>
                            Host Panel
                          </span>
                        ) : (
                          <span className="badge badge-secondary" style={{ fontSize: "11px" }}>
                            User App
                          </span>
                        )}
                      </td> */}
                            <td style={{ padding: "8px 10px" }} data-label="Status">{getBookingStatus(booking)}</td>
                            <td style={{ padding: "8px 10px" }} data-label="Action">
                              {/* Only show Approve for user-app bookings that aren't yet approved and session hasn't expired */}
                              {booking.bookingSource !== "host_panel" &&
                                !booking.hostApproved &&
                                booking.status !== "cancelled" &&
                                !isSessionExpired(booking) && (
                                  <button
                                    className="btn btn-primary btn-xxs"
                                    onClick={() => handleApprove(booking._id)}
                                  >
                                    Approve
                                  </button>
                                )}
                              {booking.hostApproved && (
                                <span className="text-success small">
                                  <i className="fas fa-check-circle me-1"></i>
                                  {booking.bookingSource === "host_panel" ? "Auto-Approved" : "Verified"}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                    <span className="text-muted small">
                      Page {pagination.currentPage} of {pagination.totalPages} — Total{" "}
                      {pagination.total} bookings
                    </span>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={pagination.currentPage === 1}
                        onClick={handlePrevPage}
                      >
                        ← Prev
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={pagination.currentPage === pagination.totalPages}
                        onClick={handleNextPage}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Booking Modal (portal) ── */}
      {showCreateModal &&
        ReactDOM.createPortal(
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              if (!creating) setShowCreateModal(false);
            }}
          >
            <div
              className={`p-4 ${isDark ? "text-white" : "text-dark"}`}
              style={{
                width: 500,
                maxWidth: "100%",
                backgroundColor: isDark ? "#1e1e2e" : "#ffffff",
                borderRadius: "15px",
                boxShadow: "0 15px 35px rgba(0,0,0,0.3)",
                border: isDark ? "1px solid #333" : "1px solid #eee",
                position: "relative"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <i className="fas fa-calendar-plus me-2 text-success"></i>
                  Create Booking
                </h5>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  ✕
                </button>
              </div>

              <p className="text-muted small mb-3">
                Booking for <strong>{domeData?.domeName}</strong>. Once created, it
                will be auto-approved and visible on the tablet.
              </p>

              {/* Date picker */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="fas fa-calendar me-1"></i> Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={createForm.date}
                  min={getDomeTodayDateString()}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>

              {/* Session slot dropdown */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="fas fa-clock me-1"></i> Time Slots
                </label>
                {!createForm.date ? (
                  <div className="text-muted small border p-2 rounded" style={{ backgroundColor: isDark ? "#2a2a3c" : "#f8f9fa" }}>
                    Please select a date first to view available sessions.
                  </div>
                ) : sessionsLoading ? (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    Loading sessions...
                  </div>
                ) : (
                  <select
                    className="form-select"
                    value={createForm.sessionRef}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, sessionRef: e.target.value }))
                    }
                  >
                    <option value="">— Select a time slot —</option>
                    {filteredSessions.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name
                          ? `${s.name} — ${s.startTime || ""}`
                          : s.startTime || s._id}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {createError && (
                <div className="alert alert-danger py-2">{createError}</div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleCreateBooking}
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-1"></i> Create Booking
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DomeBookings;
